import { HttpParams } from '@angular/common/http';

import type { DashboardTile, InputApiCallConfig } from './dvm-layout-builder.types';
import { isChartTile } from './dvm-tile-crossfilter.utils';
import { flattenTiles } from './dvm-tile-groups.utils';

/**
 * Pure helpers behind an input control's "custom API call" and the recompute
 * fan-out: config normalisation, token substitution into a body template,
 * merging a custom response with the compute response, and picking the
 * tile a result entry belongs to.
 */

export function defaultApiCallConfig(): InputApiCallConfig {
  return {
    enabled: false,
    url: '',
    method: 'POST',
    bodyTemplate: '{\n  "value": "{value}",\n  "componentId": "{componentId}"\n}',
    mode: 'replace',
    targets: [],
  };
}

export function sanitizeApiCallConfig(config: InputApiCallConfig): Record<string, unknown> {
  return {
    enabled: !!config.enabled,
    url: config.url?.trim() ?? '',
    method: config.method || 'POST',
    bodyTemplate: stripShellQuotes(config.bodyTemplate ?? ''),
    mode: config.mode === 'parallel' ? 'parallel' : 'replace',
    targets: (config.targets ?? []).map(String).filter(Boolean),
  };
}

export function restoreApiCallConfig(raw: unknown): InputApiCallConfig | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const source = raw as Record<string, unknown>;
  const fallback = defaultApiCallConfig();
  const method = String(source['method'] ?? fallback.method).toUpperCase();
  return {
    enabled: source['enabled'] === true,
    url: String(source['url'] ?? ''),
    method: (['GET', 'POST', 'PUT', 'PATCH'].includes(method)
      ? method
      : fallback.method) as InputApiCallConfig['method'],
    bodyTemplate: String(source['bodyTemplate'] ?? fallback.bodyTemplate),
    mode: source['mode'] === 'parallel' ? 'parallel' : 'replace',
    targets: Array.isArray(source['targets'])
      ? (source['targets'] as unknown[]).map(String).filter(Boolean)
      : [],
  };
}

/** The payload's targets minus the ones a custom API answers for. */
export function targetsExcluding(payload: Record<string, unknown>, excluded: Set<string>): Array<Record<string, unknown>> {
  const targets = Array.isArray(payload?.['targets']) ? (payload['targets'] as Array<Record<string, unknown>>) : [];
  return targets.filter((target) => !excluded.has(String(target?.['componentId'] ?? '')));
}

/** Custom entries win; compute entries for the custom-owned components are dropped. */
export function mergeComputeResponses(
  customResponse: Record<string, unknown> | null,
  computeResponse: Record<string, unknown> | null,
  customIds: Set<string>,
): Record<string, unknown> {
  const entriesOf = (response: Record<string, unknown> | null): Array<Record<string, unknown>> =>
    Array.isArray(response?.['results']) ? (response!['results'] as Array<Record<string, unknown>>) : [];
  const customEntries = entriesOf(customResponse);
  const ownedByCustom = new Set(
    customEntries.map((entry) => String(entry?.['componentId'] ?? '')).filter(Boolean),
  );
  const computeEntries = entriesOf(computeResponse).filter((entry) => {
    const id = String(entry?.['componentId'] ?? '');
    return !ownedByCustom.has(id) && !customIds.has(id);
  });
  return {
    ...(customResponse ?? {}),
    ...(computeResponse ?? {}),
    results: [...customEntries, ...computeEntries],
  };
}

/** A body copied out of a curl command keeps the shell quotes around it (`'{...}'`), which makes it invalid JSON. */
export function stripShellQuotes(template: string): string {
  const trimmed = String(template ?? '').trim();
  const start = trimmed.search(/[{[]/);
  const end = Math.max(trimmed.lastIndexOf('}'), trimmed.lastIndexOf(']'));
  if (start < 0 || end < start) return trimmed;
  const outside = trimmed.slice(0, start) + trimmed.slice(end + 1);
  return /^['"`\s]*$/.test(outside) ? trimmed.slice(start, end + 1) : trimmed;
}

/** Substitute `{token}` placeholders into the JSON body template; falls back to the raw tokens when the result is not JSON. */
export function buildApiCallBody(bodyTemplate: string | undefined, tokens: Record<string, unknown>): unknown {
  const template = stripShellQuotes(String(bodyTemplate ?? '')) || '{}';
  const filled = template
    .replace(/\[\s*"\{(\w+)\}"\s*\]/g, (match, key) =>
      (key in tokens && Array.isArray(tokens[key]) ? JSON.stringify(tokens[key]) : match))
    .replace(/"\{(\w+)\}"/g, (match, key) => (key in tokens ? JSON.stringify(tokens[key]) : match))
    .replace(/\{(\w+)\}/g, (match, key) => {
      if (!(key in tokens)) return match;
      const token = tokens[key];
      return token !== null && typeof token === 'object' ? JSON.stringify(token) : String(token ?? '');
    });
  warnUnresolvedTokens(filled, tokens);
  try {
    return JSON.parse(filled);
  } catch {
    console.warn('[dvm] custom API body template is not valid JSON after token substitution; sending tokens instead:', filled);
    return { ...tokens };
  }
}

/** An unsubstituted `{token}` still parses as JSON, so the endpoint silently receives the literal
 *  instead of a value. Name the misses and what WAS available, rather than failing quietly. */
function warnUnresolvedTokens(filled: string, tokens: Record<string, unknown>): void {
  const unresolved = [...filled.matchAll(/\{(\w+)\}/g)].map((m) => m[1]).filter((key) => !(key in tokens));
  if (!unresolved.length) return;
  console.warn(
    `[dvm] custom API body template has unresolved token(s): ${[...new Set(unresolved)].join(', ')}.`,
    'Available tokens:', Object.keys(tokens).sort(),
  );
}

export function buildApiCallParams(tokens: Record<string, unknown>): HttpParams {
  let params = new HttpParams();
  Object.entries(tokens).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((entry) => { params = params.append(key, String(entry)); });
    } else {
      params = params.set(key, String(value));
    }
  });
  return params;
}

/** Current values of every OTHER input control on the dashboard (sent along with a recompute).
 *  Controls inside a GROUP card count too — the published view sees them as flat widgets, so
 *  skipping them here left their `{token}`s unresolved in the builder only. */
export function otherActiveInputValues(tiles: DashboardTile[], excludeComponentId: string): Array<{ componentId: string; title: string; field: string; value: unknown }> {
  return flattenTiles(tiles)
    .filter((item) => !isChartTile(item) && item.layoutType !== 'FILE_UPLOAD' && item.layoutType !== 'POPUP' && item.componentId !== excludeComponentId)
    .map((item) => ({
      componentId: item.componentId,
      title: item.title || '',
      field: String((item.payload as Record<string, unknown> | undefined)?.['field'] ?? item.dropdownControl?.field ?? ''),
      value: (item.payload as Record<string, unknown> | undefined)?.['value']
        ?? item.dropdownControl?.value
        ?? item.inputControl?.value
        ?? (item.payload as Record<string, unknown> | undefined)?.['defaultValue']
        ?? null,
    }))
    .filter((entry) => !!entry.componentId);
}

/** Every OTHER input's live value as a flat `{field: value}` map, so a custom
 *  API `bodyTemplate` can reference a sibling control by the column it drives
 *  (e.g. `{spec_name}` for a Spec Name dropdown). Own-payload keys still win.
 *
 *  A single-select dropdown holds its selection as a one-element array, but a
 *  backend filtering on that column wants the scalar — sending `["73022.02"]`
 *  stringifies server-side to `[73022.02]` and matches nothing. Multi-select
 *  selections stay arrays, since there the list IS the value. */
export function crossInputTokens(tiles: DashboardTile[], excludeComponentId: string): Record<string, unknown> {
  const tokens: Record<string, unknown> = {};
  otherActiveInputValues(tiles, excludeComponentId).forEach((entry) => {
    const value = Array.isArray(entry.value) && entry.value.length === 1 ? entry.value[0] : entry.value;
    // `field` is only present when the stored layout carried it, so a control is also addressable by
    // its snake_cased title ("Spec Name" → spec_name) and by its componentId.
    [entry.field, ...titleAliases(entry.title), entry.componentId]
      .filter((alias): alias is string => !!alias)
      .forEach((alias) => {
        if (!(alias in tokens)) tokens[alias] = value;
      });
  });
  return tokens;
}

/** "SSA $" and "SSA %" both reduce to "ssa" once punctuation is stripped, so the unit is spelled out
 *  first to keep them apart; the bare form is kept too, for titles where it is unambiguous. */
function titleAliases(title: string): string[] {
  const spelled = snakeCase(String(title ?? '').replace(/%/g, ' pct ').replace(/\$/g, ' dollar '));
  const bare = snakeCase(title);
  return spelled === bare ? [bare] : [spelled, bare];
}

function snakeCase(text: string): string {
  return String(text ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Domain rule: changing the "SSA $" input also re-runs the "Price inflation %" and "Cost inflation %" inputs first. */
export function sideEffectInputsFor(tile: DashboardTile, tiles: DashboardTile[]): DashboardTile[] {
  const title = String(tile?.title ?? '').trim().toLowerCase();
  if (title !== 'ssa $') return [];
  const wanted = new Set(['price inflation %', 'cost inflation %']);
  return flattenTiles(tiles).filter((item) => !isChartTile(item) && item.layoutType !== 'FILE_UPLOAD' && item.layoutType !== 'POPUP'
    && item.componentId !== tile.componentId
    && wanted.has(String(item.title ?? '').trim().toLowerCase()));
}

/** The tile a recompute result entry addresses: by componentId, else the single tile of the entry's chart type. */
export function findRecomputeTargetTile(allTiles: DashboardTile[], entryComponentId: string, chartType: string, allowedIds: Set<string>): DashboardTile | undefined {
  if (entryComponentId && (!allowedIds.size || allowedIds.has(entryComponentId))) {
    const direct = allTiles.find((tile) => tile.componentId === entryComponentId);
    if (direct) return direct;
  }

  if (!chartType) return undefined;
  const candidates = allowedIds.size
    ? allTiles.filter((tile) => allowedIds.has(String(tile.componentId ?? '')))
    : allTiles;
  const sameType = candidates.filter((tile) => tile.layoutType === chartType);
  return sameType.length === 1 ? sameType[0] : undefined;
}

/** Base64 payload of a picked file (data-URL prefix stripped). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
